import type {FunctionComponent} from 'react'
import React, {useMemo, useState} from 'react'
import {useOutletContext, useRouteLoaderData, useSearchParams} from '@remix-run/react'
import clsx from 'clsx'
import {Field, Listbox, ListboxButton, ListboxOption, ListboxOptions} from '@headlessui/react'
import {getTranslation} from '~/localization/translation'
import type {RootAndListContext, RootLoaderResponse, SortingOption} from '~/types'
import {useReportCustomEvent, useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import ShareMenu from '~/components/commons/share-menu'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'
import {getDomainConfig} from '~/domains'
import {getUtcDateDaysAgo} from '~/utils/commons'
import BreadcrumbsHeaderNewLayout from '../breadcrumbs/breadcrumbs-header-new-layout'
import AffiliateDisclosureHeaderNewLayout from './affiliate-disclosure-header-new-layout'

interface ProductsHeaderNewLayoutProps {
  sortBy: SortingOption | null
  setSortBy: React.Dispatch<React.SetStateAction<SortingOption | null>>
}

const DEFAULT_SORTING_OPTION = 'editorsChoice' as SortingOption

const ProductsHeaderNewLayout: FunctionComponent<ProductsHeaderNewLayoutProps> = ({sortBy, setSortBy}) => {
  const {
    dataResponse: {productsCategory, products},
  } = useOutletContext<RootAndListContext>()
  const {language, deviceType, featureFlags, domain, isUSLocale, routeName, locale, renderTimestamp} =
    useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const [onHoverOnceReportedElements, setOnHoverOnceReportedElements] = useState<string[]>([])
  const reportWidgetClick = useReportWidgetClick()
  const reportCustomEvent = useReportCustomEvent()
  const [, setSearchParams] = useSearchParams()
  const [fallbackRenderTimestamp] = useState(() => Date.now())

  const {domainType, styles: stylesBydomain} = getDomainConfig(domain, locale)
  const isInsiderDomain = domainType === 'insider'
  const productsCount = products.slice(0, 10).length

  const dateFormatter = useMemo(
    () => ({
      short: new Intl.DateTimeFormat(language, {month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC'}),
      long: new Intl.DateTimeFormat(language, {month: 'long', day: '2-digit', year: 'numeric', timeZone: 'UTC'}),
    }),
    [language],
  )

  const formatDate = (timestamp: number, monthLength: 'long' | 'short' = 'long') => {
    const date = new Date(timestamp)
    return dateFormatter[monthLength].format(date)
  }

  const [sortWidgetRef] = useReportWidgetSeen({
    widget_name: 'sorting',
    widget_place: 'top',
  })

  const baseRenderTimestamp = Number.isFinite(renderTimestamp) ? renderTimestamp : fallbackRenderTimestamp
  const twoDaysAgo = getUtcDateDaysAgo(baseRenderTimestamp, 2)
  const twoDaysAgoMHYShort = formatDate(twoDaysAgo.getTime(), 'short')
  const currentSortOptionLabel =
    translations.Header.sorting.options.find((o) => o.value === sortBy)?.label ?? translations.Header.sorting.title

  const onHover = (elementName: string, variation: string) => {
    if (deviceType === 'desktop' && !onHoverOnceReportedElements.includes(variation)) {
      reportCustomEvent('hover', {
        elementName,
        placement: 'top',
        variation,
      })
      setOnHoverOnceReportedElements([...onHoverOnceReportedElements, variation])
    }
  }

  const defaultTitle = isUSLocale
    ? `${translations.Header.titleFormat(productsCount, productsCategory?.name, featureFlags?.showEngagingElements)}`
    : `${translations.Header.best(productsCount, productsCategory?.gender)} ${productsCategory?.name}`

  const dealsTitle = `${translations.Header.titleDealsFormat(productsCount, productsCategory?.name)}`

  return (
    <div
      className={clsx(
        'mb-10 flex w-full flex-col border-b border-neutral-200 py-4 shadow-sm',
        featureFlags?.showNewHeaderAndCookieUi && deviceType !== 'desktop' ? 'mb-4' : '',
        isInsiderDomain ? 'mb-4' : '',
      )}
      data-testid="list-header"
    >
      <div
        className={clsx(
          'utils_main_container mx-auto flex w-full flex-col justify-center gap-4 mobile:gap-3',
          stylesBydomain.UTILS_MAIN_CONTAINER,
        )}
      >
        <div className={clsx('flex items-center', isUSLocale ? 'h-6' : 'hidden')}>
          {isUSLocale && <BreadcrumbsHeaderNewLayout />}
        </div>

        <div
          className="flex flex-row items-center gap-2"
          aria-hidden="true"
          onClick={() => {
            reportWidgetClick({
              widget_name: 'info-header',
              widget_place: 'top',
              trigger_type: 'last-updated',
            })
          }}
          onMouseEnter={() => onHover('info-header', 'last-updated')}
        >
          <span className="text-sm font-extralight text-[#4F4F4F]" data-testid="last-updated">
            {translations.Header.updatedText}: {twoDaysAgoMHYShort}
          </span>
        </div>
        <h1
          data-testid="category-title"
          className="text-2xl font-[500] leading-6 text-black mobile:text-2xl mobile:leading-[28px]"
        >
          {domainType === 'deals' ? dealsTitle : defaultTitle}
        </h1>
        <div className="flex w-full flex-row items-center justify-between">
          {isUSLocale ? <AffiliateDisclosureHeaderNewLayout /> : null}
          <div
            className={clsx(
              'ml-auto flex flex-row items-center gap-3',
              routeName === 'multi_lang_slug_page' && 'tablet:gap-1',
            )}
          >
            <ShareMenu
              onHover={onHover}
              placement="brg_above_the_list"
              shouldShowNewIcon={true}
              className="!size-[30px] [&>div>button>svg]:!size-[30px] [&>div>button>svg]:!p-[7px] [&>div>button>svg]:shadow-elegant [&>div]:mr-0"
            />
            <div
              className={clsx(
                'self-start rounded-lg text-sm font-light text-[#4F4F4F] shadow-elegant',
                stylesBydomain.LISTPAGE_WIDGETS_SORT_BY_BUTTON,
                'listpage_widgets_sort_by_button',
              )}
              ref={sortWidgetRef}
            >
              <Field>
                <Listbox
                  value={sortBy}
                  onChange={(value) => {
                    reportWidgetClick({
                      widget_name: 'sorting-option',
                      widget_place: 'top',
                      trigger_type: value ?? '',
                    })
                    setSortBy(value)
                    if (value)
                      setSearchParams((prev) => {
                        prev.set('sortBy', value)
                        return prev
                      })
                  }}
                >
                  <ListboxButton
                    className={clsx(
                      'flex items-center gap-2 px-3 py-1 mobile:max-w-32 [&>svg]:rotate-90 [&>svg]:data-[open]:rotate-[270deg]',
                      sortBy &&
                        `${stylesBydomain.LISTPAGE_WIDGETS_SORT_BY_CURRENT_OPTION_LABEL} listpage_widgets_sort_by_current_option_label`,
                    )}
                  >
                    <span className="text-start mobile:line-clamp-1"> {currentSortOptionLabel}</span>
                    <IconChevronRightThick fill="#3F3F46" className="size-4 transition duration-200 ease-out" />
                  </ListboxButton>
                  <ListboxOptions
                    anchor="bottom end"
                    className="z-40 mt-2 min-w-40 origin-top-right rounded-lg border border-neutral-100 bg-white py-2 shadow-lg transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                  >
                    {translations.Header.sorting.options.map((option) => {
                      const isSelected =
                        (sortBy && option?.value === sortBy) || (!sortBy && option?.value === DEFAULT_SORTING_OPTION)
                      return (
                        <ListboxOption
                          key={option.id}
                          value={option.value}
                          className={clsx(
                            'block w-full cursor-pointer px-3 py-2 text-start text-sm',
                            isSelected
                              ? `${stylesBydomain.LISTPAGE_WIDGETS_SORT_BY_SELECTED_OPTION} listpage_widgets_sort_by_selected_option`
                              : `${stylesBydomain.LISTPAGE_WIDGETS_SORT_BY_OPTION} listpage_widgets_sort_by_option`,
                          )}
                        >
                          {option.label}
                        </ListboxOption>
                      )
                    })}
                  </ListboxOptions>
                </Listbox>
              </Field>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductsHeaderNewLayout
